'use client'

import React, { useState, useEffect } from 'react'
import Footer from './Footer'
import {
  Box, Stack, HStack, Heading, Text, VStack, useColorModeValue,
  List, ListItem, ListIcon, Button, Input, useToast, Divider,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, FormControl, FormLabel, Select, FormHelperText, FormErrorMessage,
} from '@chakra-ui/react'
import { FaCheckCircle } from 'react-icons/fa'
import axios from 'axios'
import { BackgroundBeams } from '../ui/background-beams.tsx'
import { Helmet } from 'react-helmet-async' // ✅ keep production SEO

// Email validation (used before sanitize)
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

// ✅ keep production base URL/env
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || `/api`

function PriceWrapper({ children }) {
  const borderColor = '#e500ff'
  return (
    <Box
      mb={4}
      shadow="base"
      borderWidth="2px"
      alignSelf={{ base: 'center', lg: 'flex-start' }}
      borderColor={borderColor}
      borderRadius="xl"
      bg="#000000"
    >
      {children}
    </Box>
  )
}

export default function SubscribePage() {
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [tier, setTier] = useState('')
  const [regulation, setRegulation] = useState('')
  const [name, setName] = useState('')
  const [surname, setSurname] = useState('')
  const [frequency, setFrequency] = useState('')
  const [timezone, setTimezone] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [regulationError, setRegulationError] = useState('')

  const cardBg = useColorModeValue('gray.50', 'gray.600')
  const badgeBg = '#00ffb1'

  const buttonBorderColor = '#bd00ff'
  const buttonHoverColor = '#d857ff'

  // Lighter background for Basic tier
  const basicBg = useColorModeValue('gray.10', 'gray.600')
  const basicBottomBg = useColorModeValue('gray.300', 'gray.400')

  // Allow letters, numbers, space, comma, period, hyphen
  const allowedRegulationChars = /^[a-zA-Z0-9\s.,-]+$/

  const tiers = [
    {
      name: 'Basic',
      price: '150.00',
      features: ['1 Regulation', 'Email Support', 'Monthly Updates', 'Cancel Anytime'],
      buttonText: 'Subscribe',
      isPopular: false,
      background: basicBg,
      bottomBackground: basicBottomBg,
    },
    {
      name: 'Standard',
      price: '500.00',
      features: ['5 Regulations', 'Priority Support', 'Monthly or Weekly Updates', 'Cancel Anytime'],
      buttonText: 'Subscribe',
      isPopular: true,
    },
    {
      name: 'Premium',
      price: '750.00',
      features: [
        '10 Regulations',
        'AI Insights + Implementation Guide',
        'Priority Support',
        'Monthly or Weekly Updates',
        'Cancel Anytime',
      ],
      buttonText: 'Subscribe',
      isPopular: false,
    },
  ]

  // Capture user's timezone from the browser
  useEffect(() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    setTimezone(userTimezone)
  }, [])

  const sanitizeInput = (text) =>
    text.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

  // Regulation input handler with normalization + limits + error text
  const handleRegulationChange = (e) => {
    const raw = e.target.value.slice(0, 400) // hard cap for safety
    const value = sanitizeInput(raw)

    // quick character whitelist
    if (!allowedRegulationChars.test(value)) {
      setRegulation(value)
      setRegulationError('Only letters, numbers, spaces, commas, periods, and hyphens are allowed.')
      return
    }

    // normalize list: split on comma, trim, collapse spaces, remove empties
    const items = value
      .split(',')
      .map((s) => s.trim().replace(/\s+/g, ' '))
      .filter(Boolean)

    // enforce tier limits
    const tl = tier.toLowerCase()
    const limit = tl === 'basic' ? 1 : tl === 'standard' ? 5 : tl === 'premium' ? 10 : 0

    let error = ''
    if (limit && items.length > limit) {
      error = `${tier} plan allows a maximum of ${limit} regulation${limit > 1 ? 's' : ''}.`
    }

    setRegulation(items.join(', '))
    setRegulationError(error)
  }

  const resetForm = () => {
    setName('')
    setSurname('')
    setEmail('')
    setRegulation('')
    setFrequency('')
    setTier('')
    setIsModalOpen(false)
    setRegulationError('')
  }

  const openModal = (plan) => {
    setTier(plan)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    resetForm()
  }

  const currentTierLower = tier.toLowerCase()

  const handleSubmit = async () => {
    // required fields
    if (!email || !regulation || !tier || !name || !surname || (currentTierLower !== 'basic' && !frequency)) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all fields and choose a plan.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // validate email (raw), then sanitize for sending
    if (!validateEmail(email)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // block submit if regulation validation raised an error
    if (regulationError) {
      toast({
        title: 'Fix regulation(s)',
        description: regulationError,
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // final normalization before sending
    const items = regulation.split(',').map((s) => s.trim()).filter(Boolean)
    const limit = currentTierLower === 'basic' ? 1 : currentTierLower === 'standard' ? 5 : 10
    const finalItems = items.slice(0, limit)
    const normalizedRegulation = finalItems.join(', ')

    const requestData = {
      email: sanitizeInput(email),
      regulation: sanitizeInput(normalizedRegulation),
      tier: currentTierLower,
      name: sanitizeInput(name),
      surname: sanitizeInput(surname),
      frequency: currentTierLower === 'basic' ? 'monthly' : frequency,
      timezone: sanitizeInput(timezone),
    }

    try {
      // ✅ keep production flow: create user/subscription, then initiate Netcash
      const response = await axios.post(`${API_BASE_URL}/create_subscription_and_user`, requestData)
      const responseData = response.data

      if (response.status === 201 && responseData.subscription_id && responseData.user_id) {
        const netcashResponse = await axios.post(`${API_BASE_URL}/initiate_netcash_payment`, {
          user_id: responseData.user_id,
          subscription_id: responseData.subscription_id,
        })
        const { redirect_url_base, payload } = netcashResponse.data

        // Hidden form POST redirect to Netcash
        const form = document.createElement('form')
        form.method = 'POST'
        form.action = redirect_url_base
        form.style.display = 'none'

        for (const [key, value] of Object.entries(payload || {})) {
          if (value !== undefined && value !== null) {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.name = key
            input.value = value
            form.appendChild(input)
          }
        }

        document.body.appendChild(form)
        setTimeout(() => form.submit(), 100)
        return
      }

      toast({
        title: 'Subscribed!',
        description: 'You will receive updates based on your plan.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      resetForm()
    } catch (error) {
      console.error('❌ Error during subscription:', error)
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  return (
    <>
      {/* ✅ keep production SEO */}
      <Helmet>
        <title>Subscribe | Gemini Cybersecurity Compliance Updates</title>
        <meta
          name="description"
          content="Subscribe to receive compliance updates (weekly, monthly, or quarterly) with clear next steps for your organisation."
        />
        <link rel="canonical" href="https://gemini-cybersecurity.com/subscribe" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <Box py={12} px={6} bg="blackAlpha.900" color="white" minH="100vh">
        <BackgroundBeams />

        <VStack spacing={2} textAlign="center" mb={10}>
          <Heading as="h1" fontSize="4xl" color="white">
            Select A Plan That Fits Your Need
          </Heading>
          <Text fontSize="lg" color="gray.500">
            Receive Regular Updates on the selected regulations of your choosing on a Weekly, Monthly or Quarterly basis and Cancel anytime.
          </Text>
        </VStack>

        <Stack
          direction={{ base: 'column', md: 'row' }}
          textAlign="center"
          justify="center"
          spacing={{ base: 4, lg: 10 }}
          py={10}
        >
          {tiers.map((plan) => (
            <PriceWrapper key={plan.name}>
              <Box position="relative">
                {plan.isPopular && (
                  <Box position="absolute" top="-16px" left="50%" transform="translateX(-50%)">
                    <Text bg={badgeBg} px={3} py={1} color="black" fontSize="sm" fontWeight="600" rounded="xl">
                      Most Popular
                    </Text>
                  </Box>
                )}

                <Box
                  py={4}
                  px={12}
                  bg={plan.background || '#000000'}
                  borderTopRadius="xl"
                  borderBottomLeftRadius="0"
                  borderBottomRightRadius="0"
                >
                  <Text fontWeight="500" fontSize="2xl" color="white" />
                </Box>

                <Box bg={cardBg} shadow="base" borderBottomRadius="xl" bgColor="#000000">
                  <Box py={4} px={12}>
                    <Text fontWeight="semibold" style={{ color: 'rgb(0, 184, 255)' }} fontSize="2xl">
                      {plan.name}
                    </Text>
                    <HStack justifyContent="center">
                      <Text fontSize="5xl" fontWeight="600">
                        R
                      </Text>
                      <Text fontSize="5xl" fontWeight="900">
                        {plan.price}
                      </Text>
                      <Text fontSize="3xl" color="gray.500">
                        /month
                      </Text>
                    </HStack>
                  </Box>

                  <VStack bg="#000000" py={4} borderBottomRadius="xl">
                    <List spacing={3} textAlign="start" px={12}>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index}>
                          <ListIcon as={FaCheckCircle} color="green.500" />
                          {feature}
                        </ListItem>
                      ))}
                    </List>

                    <Box w="80%" pt={7}>
                      <Button
                        w="full"
                        colorScheme="red"
                        variant={plan.isPopular ? 'solid' : 'outline'}
                        onClick={() => openModal(plan.name)}
                        sx={{
                          borderColor: buttonBorderColor,
                          _hover: plan.isPopular
                            ? { bg: 'red.600' }
                            : { bg: 'transparent', borderColor: buttonHoverColor, color: buttonHoverColor },
                          ...(plan.isPopular
                            ? { bg: 'red.500', color: 'white' }
                            : { bg: 'transparent', color: 'white' }),
                        }}
                      >
                        {plan.buttonText}
                      </Button>
                    </Box>
                  </VStack>
                </Box>
              </Box>
            </PriceWrapper>
          ))}
        </Stack>

        {/* Modal for Subscription Form */}
        {tier && (
          <Modal isOpen={isModalOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent bg="white" color="black" rounded="lg">
              <ModalHeader>Complete Your Subscription</ModalHeader>
              <ModalCloseButton
                color="red.600"
                fontSize="l"
                fontWeight="bold"
                border="2px solid red"
                borderRadius="full"
                w="8"
                h="8"
                top="4"
                right="4"
                _hover={{ color: 'red.100', transform: 'scale(1.1)' }}
                _focus={{ boxShadow: 'none' }}
              />
              <ModalBody>
                <FormControl mb={4}>
                  <FormLabel>Name</FormLabel>
                  <Input
                    placeholder="Your Name"
                    value={name}
                    onChange={(e) => setName(sanitizeInput(e.target.value))}
                  />
                </FormControl>

                <FormControl mb={4}>
                  <FormLabel>Surname</FormLabel>
                  <Input
                    placeholder="Your Surname"
                    value={surname}
                    onChange={(e) => setSurname(sanitizeInput(e.target.value))}
                  />
                </FormControl>

                <FormControl mb={4}>
                  <FormLabel>Email Address</FormLabel>
                  <Input placeholder="Your Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </FormControl>

                {/* Regulation(s) field WITH helper/error text */}
                <FormControl mb={4} isInvalid={!!regulationError}>
                  <FormLabel>Regulation(s)</FormLabel>
                  <Input
                    placeholder="ISO 27001, POPIA, GDPR, DORA"
                    value={regulation}
                    onChange={handleRegulationChange}
                    pattern={allowedRegulationChars.source}
                    title="Only letters, numbers, spaces, commas, periods, and hyphens are allowed."
                    maxLength={400}
                  />

                  {regulationError ? (
                    <FormErrorMessage>{regulationError}</FormErrorMessage>
                  ) : (
                    <FormHelperText>
                      {currentTierLower === 'basic' && 'Basic plan allows a maximum of 1 regulation.'}
                      {currentTierLower === 'standard' &&
                        'Standard plan allows a maximum of 5 regulations (separate with commas).'}
                      {currentTierLower === 'premium' &&
                        'Premium plan allows a maximum of 10 regulations (separate with commas).'}
                    </FormHelperText>
                  )}
                </FormControl>

                {currentTierLower !== 'basic' ? (
                  <FormControl mb={4}>
                    <FormLabel>Frequency</FormLabel>
                    <Select placeholder="Select Frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl mb={4}>
                    <FormLabel>Frequency</FormLabel>
                    <Select value="monthly" isDisabled>
                      <option value="monthly">Monthly</option>
                    </Select>
                    <FormHelperText>Basic plan only supports monthly updates.</FormHelperText>
                  </FormControl>
                )}
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={closeModal}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={handleSubmit} isDisabled={!!regulationError}>
                  Submit
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}

        <Divider my={10} borderColor="whiteAlpha.300" />
        <Footer />
      </Box>
    </>
  )
}
